/**
 * MUI DimensionInput Component
 * 
 * This component is the Material UI equivalent of the original DimensionInput component.
 * It provides a dimension input field with two distance inputs.
 */

import React from 'react';
import { Box, Stack, useTheme as useMuiTheme } from '@mui/material';
import { useTheme } from 'theme/ThemeContext';
import InputLabel from '../atoms/InputLabel';
import MuiDistanceInput from './DistanceInput';
import { DimsProps, DimensionInputProps, PositionInputProps, RangeInputProps } from 'types/components/molecules';

/**
 * MUI Dims component
 * 
 * @param props - Component props
 * @returns The MUI Dims component
 */
const MuiDims: React.FC<DimsProps> = (props) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  const placeholder = props.ids.length > 1;
  const [first, second] = props.ids;

  return (
    <Stack 
      direction={props.isNav ? 'column' : 'row'} 
      spacing={1} 
      alignItems="flex-start"
      className={props.className}
      sx={{
        margin: '10px 0 0 0',
      }}
    >
      {props.label && (
        <InputLabel>
          {props.label}
        </InputLabel>
      )}
      <Stack direction="row" spacing="5px" sx={{ width: '100%' }}>
        <Box sx={{
          width: second ? '50%' : 'calc(50% - 4px)',
        }}>
          <MuiDistanceInput name={first} placeholder={placeholder} {...props} />
        </Box>
        {second && (
          <Box sx={{
            width: '50%',
          }}>
            <MuiDistanceInput name={second} placeholder={placeholder} {...props} />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

/**
 * MUI DimensionInput component
 * 
 * @param props - Component props
 * @returns The MUI DimensionInput component
 */
const MuiDimensionInput: React.FC<DimensionInputProps> = (props) => {
  return <MuiDims {...props} />;
};

/**
 * MUI PositionInput component
 * 
 * @param props - Component props
 * @returns The MUI PositionInput component
 */
const MuiPositionInput: React.FC<PositionInputProps> = (props) => {
  return <MuiDims {...props} />;
};

/**
 * MUI RangeInput component
 * 
 * @param props - Component props
 * @returns The MUI RangeInput component
 */
const MuiRangeInput: React.FC<RangeInputProps> = (props) => {
  return <MuiDims {...props} />;
};

export type { DimsProps, DimensionInputProps, PositionInputProps, RangeInputProps };
export default MuiDims;
export { MuiDimensionInput, MuiPositionInput, MuiRangeInput };
